import React, { useState } from 'react';
import {
  Publication,
  OWN_AUTHOR_NAME,
  generateBibTeXEntry,
  getPrimaryLink,
  getStatusLabel,
  getTypeLabel,
} from '../data/publications';
import FigureLightbox from './FigureLightbox';
import './FeaturedPublication.css';

interface FeaturedPublicationProps {
  publication: Publication;
}

// Render the author list with own authorship emphasised
const renderAuthors = (authors: string) =>
  authors.split(' and ').map((author, i, all) => (
    <React.Fragment key={author}>
      {author === OWN_AUTHOR_NAME ? <strong>{author}</strong> : author}
      {i < all.length - 1 && <span className="fp-sep">; </span>}
    </React.Fragment>
  ));

const FeaturedPublication: React.FC<FeaturedPublicationProps> = ({ publication: pub }) => {
  const [copied, setCopied] = useState(false);
  const [openFigure, setOpenFigure] = useState<number | null>(null);
  const isNew = new Date().getFullYear() - pub.year < 1;
  const primaryLink = getPrimaryLink(pub);

  const copyBibTeX = () => {
    navigator.clipboard.writeText(generateBibTeXEntry(pub)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Older content may still carry a single `figure` instead of `figures`
  const figures = pub.figures ?? (pub.figure ? [{ src: pub.figure, alt: pub.title, caption: '' }] : []);

  return (
    <article className="fp">
      <header className="fp-header">
        <div className="fp-eyebrow">
          <span className="fp-eyebrow-label">Featured Research</span>
          {isNew && <span className="fp-pill fp-pill-new">New</span>}
          <span className="fp-pill">{getTypeLabel(pub.type)}</span>
          {pub.status && <span className="fp-pill">{getStatusLabel(pub.status)}</span>}
        </div>
        <h2 className="fp-title">
          {primaryLink
            ? <a href={primaryLink} target="_blank" rel="noopener noreferrer">{pub.title}</a>
            : pub.title}
        </h2>
        <p className="fp-authors">{renderAuthors(pub.authors)}</p>
        <p className="fp-venue"><em>{pub.venue}</em>, {pub.year}</p>
      </header>

      {pub.tagline && <p className="fp-lead">{pub.tagline}</p>}

      {figures.length > 0 && (
        <div className={`fp-figures fp-figures-${Math.min(figures.length, 2)}`}>
          {figures.map((fig, i) => (
            <figure key={fig.src} className="fp-figure">
              <button
                type="button"
                className="fp-figure-frame"
                onClick={() => setOpenFigure(i)}
                aria-label={`Enlarge figure ${i + 1}`}
              >
                <img src={fig.src} alt={fig.alt} loading="lazy" />
              </button>
              {fig.caption && (
                <figcaption>
                  <span className="fp-figure-num">Figure {i + 1}.</span> {fig.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}

      {pub.abstract && (
        <details className="fp-abstract">
          <summary>Abstract</summary>
          <p>{pub.abstract}</p>
        </details>
      )}

      <div className="fp-actions">
        {primaryLink && (
          <a href={primaryLink} target="_blank" rel="noopener noreferrer" className="fp-btn fp-btn-primary">
            Read the paper
          </a>
        )}
        {pub.doi && (
          <a href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener noreferrer" className="fp-btn">
            DOI
          </a>
        )}
        {/* "Read the paper" opens our hosted PDF when there is one, so link the publisher's copy here */}
        {pub.pdf && pub.url && (
          <a href={pub.url} target="_blank" rel="noopener noreferrer" className="fp-btn">
            Publisher
          </a>
        )}
        <button type="button" className="fp-btn" onClick={copyBibTeX}>
          {copied ? 'BibTeX copied' : 'Copy BibTeX'}
        </button>
      </div>

      {openFigure !== null && (
        <FigureLightbox
          figures={figures}
          index={openFigure}
          onIndexChange={setOpenFigure}
          onClose={() => setOpenFigure(null)}
        />
      )}
    </article>
  );
};

export default FeaturedPublication;
